import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconName,
  Input,
  Label,
  Text,
} from '@flatfile/baseline'
import { useCallback, useContext, useState } from 'react'
import { useBulkDeleteResource, useBulkUpdateResource } from '../../resources/hooks/useResourceMutation'
import { ResourceType } from '../../resources/types'
import { ResourceListContext } from '../ResourceList/ResourceListContext'
import useResourceList from '../../resources/hooks/useResourceList'
import { renderMappedIcon } from '../Shared/IconMapper'
import { capitalize } from '../../utils/stringUtils'

interface IBulkAction {
  entity?: string
  entityPlural?: string
  resourceType: ResourceType
  params?: any
  onBulkActionComplete: () => void
}

const DeleteAction = ({ entity, entityPlural, resourceType, onBulkActionComplete }: IBulkAction) => {
  const { bulkSelectionIds } = useContext(ResourceListContext)
  const [bulkDeleteSpace, isBulkDeleting] = useBulkDeleteResource(resourceType)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Don't render anything if no items are selected
  if (bulkSelectionIds.length === 0) return null

  const actionClicked = async () => {
    await bulkDeleteSpace({ spaceIds: bulkSelectionIds })
    setConfirmOpen(false)
    onBulkActionComplete()
  }

  return (
    <>
      <Button
        className='text-sm'
        variant='ghost'
        size='sm'
        onClick={() => setConfirmOpen(true)}
        aria-label={`Delete ${entity}`}
        icon='trash-can-2'
        iconPlacement='left'
      >
        Delete {bulkSelectionIds.length} {bulkSelectionIds.length > 1 ? entityPlural : entity}
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Proceed with bulk deletion?</DialogTitle>
          </DialogHeader>
          <div>
            <Text>
              You are about to permanently delete{' '}
              {bulkSelectionIds.length > 1 ? `${bulkSelectionIds.length} ${entityPlural}` : `this ${entity}`}. This
              action cannot be reversed. <b>Are you sure you want to continue?</b>
            </Text>
            <div className='h-6'></div>
            <Button variant='destructive' className='w-full' onClick={actionClicked} disabled={isBulkDeleting}>
              Yes, Delete Permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const MoveAction = ({ entity, entityPlural, resourceType, onBulkActionComplete, params }: IBulkAction) => {
  const { app } = params ?? {}
  const { bulkSelectionIds } = useContext(ResourceListContext)
  const [bulkMoveSpace, isMoving] = useBulkUpdateResource(resourceType)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Don't render anything if no items are selected
  if (bulkSelectionIds.length === 0) return null

  const appsResponse = useResourceList('APPS').response
  const apps = appsResponse ? appsResponse.data : []

  const otherApps = apps.filter((a: any) => ![app?.name!, 'Mapping'].includes(a.name))

  const [search, setSearch] = useState('')
  const [selectedApp, setSelectedApp] = useState<string | null>(otherApps.length > 0 ? otherApps[0].name : null)

  const actionClicked = async () => {
    const targetApp = otherApps.find((a) => a.name === selectedApp)
    await bulkMoveSpace({ spaceIds: bulkSelectionIds, appId: targetApp?.id })
    setConfirmOpen(false)
    onBulkActionComplete()
  }

  return (
    <>
      <Button
        className='text-sm'
        variant='ghost'
        size='sm'
        onClick={() => setConfirmOpen(true)}
        aria-label={`Move ${entity}`}
        icon='arrow-up-down-left-right'
        iconPlacement='left'
      >
        Move {bulkSelectionIds.length} {bulkSelectionIds.length > 1 ? entityPlural : entity}
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Proceed with bulk move?</DialogTitle>
          </DialogHeader>
          <div>
            <Text className='mb-4 text-sm'>
              Select an App to move {bulkSelectionIds.length}{' '}
              {bulkSelectionIds.length > 1 ? `these ${entityPlural}` : `this ${entity}`}. This action can be undone at
              any time by simply moving it again.
            </Text>
            <div className='mb-4 space-y-2'>
              <Label>Apps</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' className='w-full justify-between'>
                    <div className='flex items-center'>
                      {selectedApp &&
                        renderMappedIcon({
                          iconName: (otherApps.find((a) => a.name === selectedApp)?.icon as IconName) || 'app',
                        })}
                      <span className='ml-2'>{selectedApp || 'Select App'}</span>
                    </div>
                    <Icon name='chevron-bottom' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='w-[200px]'>
                  <div className='p-2'>
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.currentTarget.value)}
                      placeholder='Search for an app'
                      className='mb-2'
                    />
                  </div>
                  <div className='max-h-[200px] overflow-y-auto'>
                    {otherApps
                      .filter((a) => a.name.toLowerCase().includes(search.toLowerCase().trim()))
                      .map((a) => (
                        <DropdownMenuItem
                          key={a.id}
                          onClick={() => setSelectedApp(a.name)}
                          className='flex items-center'
                        >
                          {renderMappedIcon({
                            iconName: a.icon as IconName,
                          })}
                          <span className='ml-2'>{a.name}</span>
                        </DropdownMenuItem>
                      ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button className='w-full mt-4' onClick={actionClicked} disabled={isMoving}>
              Yes, move {bulkSelectionIds.length > 1 ? 'these' : 'this'}{' '}
              {bulkSelectionIds.length > 1 ? entityPlural : entity} to {selectedApp}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const ArchiveAction = ({ entity, entityPlural, resourceType, onBulkActionComplete, params }: IBulkAction) => {
  const { action } = params
  const { bulkSelectionIds } = useContext(ResourceListContext)
  const [bulkArchiveSpaces] = useBulkUpdateResource(resourceType)

  // Don't render anything if no items are selected
  if (bulkSelectionIds.length === 0) return null

  const actionClicked = useCallback(async () => {
    await bulkArchiveSpaces({ spaceIds: bulkSelectionIds, archive: action === 'archive' })
    onBulkActionComplete()
  }, [action, bulkSelectionIds])

  return (
    <Button
      className='text-sm'
      variant='ghost'
      size='sm'
      onClick={actionClicked}
      aria-label={`Archive ${entity}`}
      icon='archive'
      iconPlacement='left'
    >
      {capitalize(action)} {bulkSelectionIds.length} {bulkSelectionIds.length > 1 ? entityPlural : entity}
    </Button>
  )
}

export const ResourceListBulkActions = ({ children }: { children: React.ReactNode }) => {
  const { bulkSelectionIds } = useContext(ResourceListContext)

  // Always render the container div, but conditionally render its children
  return (
    <div className='flex flex-1 gap-2 justify-start items-center flex-row'>
      {bulkSelectionIds && bulkSelectionIds.length > 0 ? children : null}
    </div>
  )
}

ResourceListBulkActions.DeleteAction = DeleteAction
ResourceListBulkActions.MoveAction = MoveAction
ResourceListBulkActions.ArchiveAction = ArchiveAction
export default ResourceListBulkActions
